import React, { useState, useEffect } from 'react';
import { ref, get, onValue, update } from 'firebase/database';
import { getLocalStorageItem } from 'core/helpers';
import { db } from '@react-native-firebase/database';
import StyleSheetRW from 'core/StyleSheetRW';
import { DEFAULT_USER_IMAGE } from 'res/constants';

interface User {
    id: string;
    name: string;
    email: string;
    firstName: string;
    lastName: string;
    image?: string;
}

interface UserListProps {
    onSelectUser: (user: User | null) => void;
    setShowLoader: (showLoader: boolean) => void;
}

const UserList: React.FC<UserListProps> = ({ onSelectUser, setShowLoader }) => {
    const [userList, setUserList] = useState<User[]>([]);
    const [searchText, setSearchText] = useState("");
    const [userFilteredList, setUserFilteredList] = useState<User[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const loggedinUser = getLocalStorageItem("loggedinUser");
    const [unreadMessagesCount, setUnreadMessagesCount] = useState<{ [key: string]: number }>({});

    useEffect(() => {
        if (searchText.length > 0) {
            const trimmedText = searchText.trim().toLowerCase();
            const filteredUser = userList.filter(
                (user) =>
                    user.firstName.toLowerCase().includes(trimmedText) ||
                    user.lastName.toLowerCase().includes(trimmedText)
            );
            setUserFilteredList(filteredUser);
        } else {
            setUserFilteredList(userList);
        }
    }, [searchText, userList]);

    const getAllUserList = async () => {
        try {
            if (loggedinUser) {
                const dbRef = ref(db, `profile`);
                await get(dbRef)
                    .then((snapshot) => {
                        if (snapshot.exists()) {
                            const userFetchedData = snapshot.val();
                            const records: User[] = [];
                            Object.keys(userFetchedData).forEach((userID) => {
                                const userObj = userFetchedData[userID];
                                if (userID !== loggedinUser.uid && userObj.employmentStatus === "Active") {
                                    userObj.id = userID;
                                    records.push(userObj);
                                }
                            });
                            setShowLoader(false);
                            setUserList(records);
                            setUserFilteredList(records);
                            fetchUnreadMessagesCount(records);
                            // Set the selected user from the URL if present
                            const params = new URLSearchParams(window.location.search);
                            const urlSelectedUserId = params.get('selectedUserId');
                            if (urlSelectedUserId) {
                                const selectedUser = records.find(user => user.id === urlSelectedUserId);
                                if (selectedUser) {
                                    setSelectedUserId(selectedUser.id);
                                    onSelectUser(selectedUser);
                                    // Remove selectedUserId from URL
                                    params.delete('selectedUserId');
                                    const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
                                    window.history.replaceState({}, '', newUrl);
                                }
                            }
                        } else {
                            console.log("No data available");
                            setShowLoader(false);
                        }
                    })
                    .catch((error) => {
                        console.error("Failed to retrieve data:", error);
                        setShowLoader(false);
                    });
            }
        } catch (error: any) {
            console.error("Error reading data:", error.message);
            setShowLoader(false);
        }
    };

    const fetchUnreadMessagesCount = (users: User[]) => {
        if (!loggedinUser) return;
        const counts: { [key: string]: number } = {};

        users.forEach(user => {
            const chatId = [loggedinUser.uid, user.id].sort().join('_');
            const messagesRef = ref(db, `messages/${chatId}`);

            onValue(messagesRef, (snapshot) => {
                const data = snapshot.val();
                if (data) {
                    let unreadCount = 0;
                    Object.keys(data).forEach((date) => {
                        Object.keys(data[date]).forEach((messageId) => {
                            const message = data[date][messageId];
                            if (message.receiverId === loggedinUser.uid && !message.isRead) {
                                unreadCount++;
                            }
                        });
                    });
                    counts[user.id] = unreadCount;
                } else {
                    counts[user.id] = 0;
                }
                setUnreadMessagesCount((prevCounts) => ({ ...prevCounts, [user.id]: counts[user.id] }));
            });
        });
    };

    useEffect(() => {
        setShowLoader(true);
        getAllUserList();
    }, []);

    const handleUserClick = async (user: User) => {
        if (selectedUserId === user.id) {
            setSelectedUserId(null);
            onSelectUser(null);
        } else {
            setSelectedUserId(user.id);
            onSelectUser(user);

            // Mark messages as read immediately
            const chatId = [loggedinUser.uid, user.id].sort().join('_');
            const updates: { [key: string]: any } = {};

            const messagesRef = ref(db, `messages/${chatId}`);
            const snapshot = await get(messagesRef);

            if (snapshot.exists()) {
                const data = snapshot.val();
                Object.keys(data).forEach((date) => {
                    Object.keys(data[date]).forEach((messageId) => {
                        const message = data[date][messageId];
                        if (message.receiverId === loggedinUser.uid && !message.isRead) {
                            updates[`/messages/${chatId}/${date}/${messageId}/isRead`] = true;
                        }
                    });
                });
                if (Object.keys(updates).length > 0) {
                    await update(ref(db), updates);
                }
            }

            setUnreadMessagesCount((prevCounts) => ({ ...prevCounts, [user.id]: 0 }));
        }
    };

    useEffect(() => {
        if (selectedUserId) {
            const chatId = [loggedinUser.uid, selectedUserId].sort().join('_');
            const messagesRef = ref(db, `messages/${chatId}`);

            const handleNewMessage = (snapshot: any) => {
                const data = snapshot.val();
                if (data) {
                    let unreadCount = 0;
                    Object.keys(data).forEach((date) => {
                        Object.keys(data[date]).forEach((messageId) => {
                            const message = data[date][messageId];
                            if (message.receiverId === loggedinUser.uid && !message.isRead) {
                                unreadCount++;
                            }
                        });
                    });
                    setUnreadMessagesCount((prevCounts) => ({ ...prevCounts, [selectedUserId]: unreadCount }));
                }
            };

            const unsubscribe = onValue(messagesRef, handleNewMessage);

            return () => unsubscribe();
        }
    }, [selectedUserId]);

    return (
        <div style={{...styles.container, overflowX: 'hidden', overflowY: 'auto', height: '100%'}}>
            <h2 style={styles.heading}>Users</h2>
            <div style={{paddingRight: "15px"}}>
                <input
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{...styles.input, width: "100%"}}
                    type="text"
                    placeholder="Enter Employee name"
                />
            </div>
            <ul style={styles.unorderList}>
                {userFilteredList.map((user) => (
                    <li key={user.id} onClick={() => handleUserClick(user)} style={{
                        ...styles.listItem,
                        backgroundColor: selectedUserId === user.id ? '#2F485833' : 'transparent',
                        fontWeight: selectedUserId === user.id ? '600' : 'normal',
                    }}>
                        <img src={user?.image || DEFAULT_USER_IMAGE} alt={`${user.firstName} ${user.lastName}`} style={styles.userImage} />
                        {user.firstName} {user.lastName}
                        {unreadMessagesCount[user.id] > 0 && (
                            <span style={styles.unreadCount}>{unreadMessagesCount[user.id]}</span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

const styles = StyleSheetRW.create(() => ({
    container: {
        fontFamily: "Outfit",
        padding: 20,
        marginBottom: 20,
        borderRight: '1px solid rgba(0, 0, 0, 0.1)',
    },
    heading: { fontWeight: 'bold', fontSize: 32, color: '#2F4858', margin: 0 },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 16,
        padding: 10,
        marginBottom: 5,
        borderRadius: 5,
        cursor: 'pointer',
    },
    unorderList: {
        listStyleType: 'none',
        padding: 0,
        margin: 0,
        marginTop: 10,
    },
    input: {
        fontSize: 16,
        fontFamily: "Outfit",
        borderTopRightRadius: 10,
        height: 45,
        outline: "none",
        borderRadius: 10,
        paddingLeft: 15,
        marginTop: 5,
        borderStyle: "none",
        backgroundColor: "#0000000D",
    },
    userImage: {
        width: 40,
        height: 40,
        borderRadius: 50,
        marginRight: 10,
        objectFit: 'cover',
        objectPosition: 'center',
        backgroundColor: '#0000000D',
    },
    unreadCount: {
        backgroundColor: '#2F4858',
        color: 'white',
        borderRadius: '50%',
        padding: '5px 10px',
        marginLeft: 'auto',
        fontSize: 12,
    }
}));

export default UserList;
